import { useCallback, useState, useRef } from 'react';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';
import { useQueryClient } from 'react-query';

import {
  CalloutType,
  RunesCalloutType,
  RUNES_CALLOUT_TYPES,
  RunesCallout,
  getErrorMessage,
  PART_BOT_RESULTS_BASE_QUERY_KEY,
  useCreatePrintExtraction,
} from '@frontend/api';
import {
  Box,
  Select,
  FormControl,
  FormLabel,
  Heading,
  CloseIcon,
  IconButton,
  useToast,
  DragHandleIcon,
  HStack,
  Container,
  ContainerHeader,
} from '@frontend/components';

import { CompositeControlFrameForm } from './forms/CompositeControlFrameForm';
import { ControlFrameForm } from './forms/ControlFrameForm';
import { CounterboreForm } from './forms/CounterboreForm';
import { CountersinkForm } from './forms/CountersinkForm';
import { DatumForm } from './forms/DatumForm';
import { DimensionForm } from './forms/DimensionForm';
import { HoleForm } from './forms/HoleForm';
import { SubmitCallback } from './types';
import type { CalloutAuthorInfo } from './useCalloutAuthor';

type CalloutTypeOption = {
  label: string;
  value: RunesCalloutType | string;
  isDisabled?: boolean;
};

const SUPPORTED_OPTIONS: CalloutTypeOption[] = [
  { label: 'Dimension', value: RUNES_CALLOUT_TYPES.DIM },
  { label: 'Control Frame', value: RUNES_CALLOUT_TYPES.CONTROL_FRAME },
  {
    label: 'Composite Control Frame',
    value: RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME,
  },
  { label: 'Hole', value: RUNES_CALLOUT_TYPES.HOLE },
  { label: 'Counterbore', value: RUNES_CALLOUT_TYPES.COUNTERBORE },
  { label: 'Countersink', value: RUNES_CALLOUT_TYPES.COUNTERSINK },
  { label: 'Datum', value: RUNES_CALLOUT_TYPES.DATUM },
];

const COMING_SOON_OPTIONS: CalloutTypeOption[] = [
  { label: 'Surface finish', value: 'surface_finish', isDisabled: true },
  { label: 'Thread', value: 'thread', isDisabled: true },
  { label: 'Flag note', value: 'flag_note', isDisabled: true },
  { label: 'Weld', value: 'weld', isDisabled: true },
  { label: 'Chamfer', value: 'chamfer', isDisabled: true },
  { label: 'Fillet', value: 'fillet', isDisabled: true },
  { label: 'Round', value: 'round', isDisabled: true },
];

const SELECT_OPTIONS = [
  {
    options: SUPPORTED_OPTIONS,
  },
  {
    label: 'Coming soon',
    options: COMING_SOON_OPTIONS,
  },
];

type CalloutAuthorProps = {
  activeFileUuid: string | null;
  calloutAuthorInfo: CalloutAuthorInfo | null;
  handleCloseCalloutAuthor: () => void;
};

export const CalloutAuthor = ({
  activeFileUuid,
  calloutAuthorInfo,
  handleCloseCalloutAuthor,
}: CalloutAuthorProps) => {
  const [selectedType, setSelectedType] = useState<CalloutTypeOption | null>(
    SUPPORTED_OPTIONS[0],
  );
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const nodeRef = useRef(null);

  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: createPrintExtraction } = useCreatePrintExtraction(
    {
      fileUuid: activeFileUuid ?? '',
      pageNumber: calloutAuthorInfo?.pageNumber ?? 0,
    },
    {
      queryOptions: {
        onSuccess: (data) => {
          console.warn('Print extraction created:', data);
          handleCloseCalloutAuthor();
          // invalidate all part bot result queries because we do not have access to the part
          queryClient.invalidateQueries(PART_BOT_RESULTS_BASE_QUERY_KEY);
          toast({
            title: 'Print extraction created',
            status: 'success',
          });
        },
        onError: (error) => {
          toast({
            title: 'Failed to create print extraction',
            description: getErrorMessage(error),
            status: 'error',
          });
        },
      },
    },
  );

  const handleSubmit: SubmitCallback = useCallback(
    (callout: CalloutType, rawText?: string) => {
      if (!calloutAuthorInfo?.loc) {
        return;
      }
      const runesCalloutPayload: RunesCallout = {
        callout,
        loc: calloutAuthorInfo.loc,
        rawText: rawText ?? '',
        desc: null,
      };
      createPrintExtraction(runesCalloutPayload);
    },
    [calloutAuthorInfo?.loc, createPrintExtraction],
  );
  const handleCancel = useCallback(() => {
    handleCloseCalloutAuthor();
  }, [handleCloseCalloutAuthor]);

  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y });
  };

  const renderSelectedType = useCallback(() => {
    return (
      <FormControl p={1}>
        <FormLabel id="callout-type-label">Callout Type</FormLabel>
        <Select
          options={SELECT_OPTIONS}
          value={selectedType}
          onChange={(value: CalloutTypeOption) => setSelectedType(value)}
          isClearable={false}
        />
      </FormControl>
    );
  }, [selectedType, setSelectedType]);

  const renderForm = useCallback(() => {
    switch (selectedType?.value) {
      case RUNES_CALLOUT_TYPES.DIM:
        return (
          <DimensionForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.CONTROL_FRAME:
        return (
          <ControlFrameForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME:
        return (
          <CompositeControlFrameForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.HOLE:
        return (
          <HoleForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.COUNTERBORE:
        return (
          <CounterboreForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.COUNTERSINK:
        return (
          <CountersinkForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      case RUNES_CALLOUT_TYPES.DATUM:
        return (
          <DatumForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            renderSelectedType={renderSelectedType}
          />
        );
      default:
        return null;
    }
  }, [selectedType, handleSubmit, handleCancel, renderSelectedType]);

  if (!calloutAuthorInfo) {
    return null;
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      onStop={handleDragStop}
      handle=".drag-handle"
      bounds="parent"
    >
      <Container
        ref={nodeRef}
        display="flex"
        flexDirection="column"
        position="absolute"
        top={16}
        left={4}
        width={45}
        pointerEvents="auto"
        shadow="popover"
        size="sm"
        variant="default"
        alignItems="stretch"
      >
        <ContainerHeader
          transition="all 0.2s ease-in-out"
          _hover={{
            bg: 'interaction.surfaceHover',
          }}
        >
          <HStack
            spacing={2}
            className="drag-handle"
            cursor="move"
            width="100%"
            justify="space-between"
          >
            <HStack spacing={1}>
              <DragHandleIcon height={2.5} color="feature.secondary" />
              <Heading m={0} size="lg">
                Add missing extraction
              </Heading>
            </HStack>
            <IconButton onClick={handleCloseCalloutAuthor} size="sm">
              <CloseIcon />
            </IconButton>
          </HStack>
        </ContainerHeader>
        <Box>{renderForm()}</Box>
      </Container>
    </Draggable>
  );
};
